import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";

import { applyPanelOverrides } from "@/components/report/panel-overrides";
import { db } from "@/integrations/firebase/firestore";
import type { DrugLibraryEntry, ReportVersionDoc } from "@/types/admin";
import type { DrugSafetyReport } from "@/types/report";

// The library is the single source of truth for what a clinician sees. A
// `drug_searches` doc carries only a POINTER (drug_id + report_version); the
// report body lives here, at `drugs/{drugId}/reports/v{latest_version}`, and a
// regenerate (admin button / seed --regenerate) bumps `latest_version` so this
// hook always resolves the current version.
export interface LibraryReport {
  report: DrugSafetyReport;
  version: number;
  generatedAt?: string;
}

interface UseLibraryReportResult {
  libraryReport: LibraryReport | null;
  loading: boolean;
  error: boolean;
  // Tri-state, because "null report" alone is ambiguous and callers act very
  // differently on the two cases: null = not settled yet (no listener has
  // fired, keep showing a loader), false = SETTLED and the library genuinely
  // holds no report for this id (drug doc missing, no versions, version doc
  // without a report body, or an unreadable read) — which the search flow turns
  // into a cache miss and regenerates. Collapsing both into
  // `{ libraryReport: null, loading: false }` is what previously let a purged
  // drug fall through to a stale embedded snapshot.
  found: boolean | null;
}

// Live-subscribe to the latest library report for a drug id. Two chained
// listeners: the drug doc gives us `latest_version`, then we stream that exact
// version doc. Never throws: a missing id, a drug with no versions, or a failed
// read all settle as `found: false` with a null report.
export function useLibraryReport(
  drugId: string | null | undefined,
): UseLibraryReportResult {
  const id = drugId ?? null;
  const [libraryReport, setLibraryReport] = useState<LibraryReport | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<boolean>(false);
  const [found, setFound] = useState<boolean | null>(id ? null : false);
  // Re-pointing at a DIFFERENT drug resets synchronously (setState-during-render
  // — React's supported "derived state" pattern) so no render ever pairs the new
  // id with the previous drug's report or a stale settled `loading`. The search
  // fast path decides "fall back to the backend" from exactly that pairing, and
  // an effect-time reset would hand it one wrong frame.
  const [trackedId, setTrackedId] = useState<string | null>(id);
  if (id !== trackedId) {
    setTrackedId(id);
    setLibraryReport(null);
    setLoading(Boolean(id));
    setError(false);
    setFound(id ? null : false);
  }

  useEffect(() => {
    // No synchronous resets here: the render-time trackedId reset above already
    // covers mount, id changes, and id -> null (setState-in-effect would
    // cascade a second render). This effect only owns the subscriptions.
    if (!drugId) return;

    let unsubReport: (() => void) | null = null;
    let currentVersion: number | null = null;

    const clearReportListener = () => {
      unsubReport?.();
      unsubReport = null;
    };

    const unsubDrug = onSnapshot(
      doc(db, "drugs", drugId),
      (snap) => {
        const drug = snap.exists()
          ? ({ drug_id: snap.id, ...snap.data() } as DrugLibraryEntry)
          : null;
        const latest = drug?.latest_version;

        if (!latest || latest < 1) {
          // Drug doc gone (purged / never seeded) or no version persisted yet:
          // settled, with nothing to show.
          clearReportListener();
          currentVersion = null;
          setLibraryReport(null);
          setLoading(false);
          setFound(false);
          return;
        }

        // Re-point the report listener only when the latest version changes.
        if (latest === currentVersion && unsubReport) return;
        currentVersion = latest;
        clearReportListener();
        setLoading(true);
        setFound(null);

        unsubReport = onSnapshot(
          doc(db, "drugs", drugId, "reports", `v${latest}`),
          (reportSnap) => {
            const data = reportSnap.exists()
              ? (reportSnap.data() as ReportVersionDoc)
              : null;
            if (!data?.report) {
              setLibraryReport(null);
              setLoading(false);
              setFound(false);
              return;
            }
            setLibraryReport({
              // Chariot 0.9 stage 8: decided panel cells show the clinician's
              // answer (no badge); reports without overrides are unchanged.
              report: applyPanelOverrides(data.report, data.panel_overrides),
              version: data.version ?? latest,
              generatedAt: data.provenance?.generated_at ?? data.created_at,
            });
            setLoading(false);
            setFound(true);
          },
          (err) => {
            console.error("Library report listener failed:", err);
            setError(true);
            setLibraryReport(null);
            setLoading(false);
            setFound(false);
          },
        );
      },
      (err) => {
        console.error("Library drug listener failed:", err);
        setError(true);
        setLibraryReport(null);
        setLoading(false);
        setFound(false);
      },
    );

    return () => {
      unsubDrug();
      clearReportListener();
    };
  }, [drugId]);

  return { libraryReport, loading, error, found };
}
